import {Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC} from 'react';
import {connect} from 'react-redux';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {OkCancelDialog} from '../../components/common/dialogs/okCancelDialog';
import {getTagChildrenIds} from '../../helpers/models/tagHelpers';
import {ResolveDispatchProps} from '../../helpers/reduxHelpers';
import {makeMapDispatchToProps} from '../../helpers/reselectHelpers';
import {TagBulkActionTypesEnum} from '../../models/api/tagBulkActionModel';
import {TagBehaviorsEnum, TagModel} from '../../models/api/tagModel';
import {SettingsTagsUrl} from '../../models/url/settingsUrlModel';
import {tagsBulkAction} from '../../store/data/tags/tagBulkActions';
import {navigateToLinkAction} from '../../store/stateless/navigationActions';
import {SettingsBannersEnum, useSettingsBanners} from '../banners/settingsBannersContext';

/*
 * Props.
 */

interface ArchiveTagDialogContainerProps {
  isInArchivedView?: boolean;
  searchQuery: string | undefined;
  tagsToActOn: ReadonlyArray<TagModel>;
  hasAllSelected: boolean;
  tagsUrl: SettingsTagsUrl | undefined;
  onRequestClose: Handler;
  onArchive: Handler;
}

/*
 * Redux.
 */

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  tagsBulkAction,
  navigateToLink: navigateToLinkAction,
}));

interface ArchiveTagDialogContainerComponentProps
  extends ArchiveTagDialogContainerProps,
    ResolveDispatchProps<typeof mapDispatchToProps> {}

const ArchiveTagDialogContainerComponent: FC<ArchiveTagDialogContainerComponentProps> = (props) => {
  const {isInArchivedView, tagsToActOn, searchQuery, hasAllSelected} = props;
  const singleTag = tagsToActOn.length === 1 ? tagsToActOn[0] : undefined;
  const {showBanner, updateBannerOnTagsBulk} = useSettingsBanners();

  const renderTitle = () => {
    if (tagsToActOn.length > 1) {
      return <Trans id="tag-edit-archiveconfirmation-headermany">Archive {tagsToActOn.length} tags</Trans>;
    }

    if (!singleTag) {
      return null;
    }

    if (isInArchivedView) {
      return (
        <Trans id="tag-edit-archiveconfirmation-headersinglearchived">
          Archive &quot;{singleTag?.name}&quot;
        </Trans>
      );
    }

    return (
      <Trans id="tag-edit-archiveconfirmation-headersingle">Archive &quot;{singleTag?.name}&quot; tag</Trans>
    );
  };

  const renderCopy = () => {
    if (singleTag) {
      return (
        <Trans id="tag-edit-archiveconfirmation-content-single">
          Archiving this tag will:
          <ul>
            {(singleTag && getTagChildrenIds(singleTag, isInArchivedView) && <li>Archive all subtags</li>) ||
              null}
            <li>Keep it on existing conversations</li>
            <li>Keep it searchable in inboxes</li>
            <li>Keep it visible in Analytics</li>
            <li>Prevent it from being added to new conversations</li>
          </ul>
        </Trans>
      );
    }

    if (tagsToActOn.length > 1) {
      return (
        <Trans id="tag-edit-archiveconfirmation-content-many">
          Archiving these tags will:
          <ul>
            <li>Archive all subtags</li>
            <li>Keep them on existing conversations</li>
            <li>Keep them searchable in inboxes</li>
            <li>Keep them visible in Analytics</li>
            <li>Prevent them from being added to new conversations</li>
          </ul>
        </Trans>
      );
    }

    return null;
  };

  const archiveSingleTag = () => {
    if (!singleTag) {
      return undefined;
    }

    return props.tagsBulkAction({
      type: TagBulkActionTypesEnum.ARCHIVE,
      namespace: props.tagsUrl ? props.tagsUrl.namespace : singleTag.namespace,
      selectedTagIds: [singleTag.id],
    });
  };

  const archiveBulkTags = () => {
    if (!props.tagsUrl) {
      return undefined;
    }

    // If we have everything selected, we only pass the search query as the backend knows we want to do a "true select all"
    if (hasAllSelected) {
      return props.tagsBulkAction({
        type: TagBulkActionTypesEnum.ARCHIVE,
        namespace: props.tagsUrl.namespace,
        searchTerm: searchQuery || '',
        searchOptions: {
          parentTagId: props.tagsUrl.tagId,
          behaviors: [TagBehaviorsEnum.CAN_UPDATE],
          excludeTagIds: _.compact([props.tagsUrl.tagId]),
          isArchived: Boolean(isInArchivedView),
        },
      });
    }

    return props.tagsBulkAction({
      type: TagBulkActionTypesEnum.ARCHIVE,
      namespace: props.tagsUrl.namespace,
      selectedTagIds: tagsToActOn.map((t) => t.id),
    });
  };

  const onOkArchiveClick = async () => {
    if (singleTag) {
      await archiveSingleTag();
    } else {
      await archiveBulkTags();
    }

    updateBannerOnTagsBulk({
      message: singleTag ? (
        <Trans id="settingstags-archivesuccess-single">Archived &quot;{singleTag.name}&quot;</Trans>
      ) : (
        <Trans id="settingstags-archivesuccess-many">Archived {tagsToActOn.length} tags</Trans>
      ),
      type: SettingsBannersEnum.SUCCESS,
    });

    showBanner({
      message: singleTag ? (
        <Trans id="settingstags-archiveloading-single">Archiving &quot;{singleTag.name}&quot;</Trans>
      ) : (
        <Trans id="settingstags-archiveloading-many">Archiving {tagsToActOn.length} tags</Trans>
      ),
      type: SettingsBannersEnum.LOADING,
      dismissAfter: 30 * 1000,
    });

    if (props.onArchive) {
      props.onArchive();
    }
  };

  return (
    <OkCancelDialog
      title={renderTitle()}
      okButtonLabel={<Trans id="tag-edit-archive">Archive</Trans>}
      okButtonVariant={ButtonVariantsEnum.PRIMARY}
      onRequestClose={props.onRequestClose}
      onOkClick={onOkArchiveClick}
    >
      {renderCopy()}
    </OkCancelDialog>
  );
};

export const ArchiveTagDialogContainer = connect(
  null,
  mapDispatchToProps,
)(ArchiveTagDialogContainerComponent);
